import { useCallback, useEffect, useState } from 'react';

import createCanonicalUrl from '~/pages/couples/explore/util/canonicalUrl';
import { LocationOrAllMarketsType } from '~/types/responseTypes';
import { setMostRecentVendorSearches } from '~/util/searchUtils';

import { PartialVendorSearch, VendorSearchRequest } from '../types/types';
import { useSearchResultsPageContext } from './SearchResultsPageContext';
import {
  NavigateToSrpFunction,
  VendorSearchNavigationContext,
} from './VendorSearchNavigationContext';

const useNavigateToSrp = () => {
  const { facetsByVendorType, setVendorSearch } = useSearchResultsPageContext();

  return useCallback(
    (vendorSearch?: VendorSearchRequest | null): NavigateToSrpFunction =>
      (change: PartialVendorSearch) => {
        let newSearch = { ...vendorSearch, ...change };

        // if the vendor type changes, we need to clear the price, capacity and any facets
        if (vendorSearch?.selectedTaxonomy?.key !== newSearch?.selectedTaxonomy?.key) {
          newSearch = {
            selectedTaxonomy: newSearch.selectedTaxonomy,
            selectedLocation: newSearch.selectedLocation,
            searchName: newSearch.searchName,
            page: 1,
          };
        } else if (change.page === undefined) {
          // We aren't going forward or backwards, we're changing filters or cities
          // So if you've done this from page 2, reset back to page 1.
          newSearch.page = 1;
        }
        const to = createCanonicalUrl(newSearch, facetsByVendorType);

        if (to === window.location.pathname + window.location.search) return; // don't add to the browser history if same URL

        setMostRecentVendorSearches(to, newSearch.selectedTaxonomy?.key);

        setVendorSearch(newSearch as VendorSearchRequest);
        window.history.pushState({}, '', to);
      },
    [facetsByVendorType, setVendorSearch]
  );
};

/**
 * Encapsulates the logic for navigating on the SRP.
 */
export const VendorSearchNavigationProvider: React.FC = ({ children }) => {
  const navigateToSrp = useNavigateToSrp();
  const { vendorSearch } = useSearchResultsPageContext();

  const [tempSelectedLocation, setTempSelectedLocation] = useState<
    LocationOrAllMarketsType | null | undefined
  >(vendorSearch.selectedLocation);
  const [updatedMarketFromUrl, setUpdatedMarketFromUrl] = useState(false);

  useEffect(() => {
    if (!updatedMarketFromUrl && vendorSearch?.selectedLocation) {
      setUpdatedMarketFromUrl(true);
      setTempSelectedLocation(vendorSearch.selectedLocation);
    }
  }, [vendorSearch?.selectedLocation, updatedMarketFromUrl]);

  return (
    <VendorSearchNavigationContext.Provider
      value={{
        navigateToSrp: navigateToSrp(vendorSearch),
        tempSelectedLocation,
        setTempSelectedLocation,
      }}
    >
      {children}
    </VendorSearchNavigationContext.Provider>
  );
};
